import React, { useState } from "react";

import { supabase } from "@/shared/lib/supabase";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { stripNameSuffix } from "@/shared/utils/names";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { DocumentType } from "@/shared/types/documentTypes";
import { getDocumentTypeLabel } from "@/shared/utils/documentTypeUtils";

interface SearchForm {
  firstName: string;
  middleName: string;
  lastName: string;
  dob: string;
  phone: string;
  email: string;
  addressLine1: string;
  addressLine2: string;
}

const INITIAL_FORM: SearchForm = {
  firstName: "",
  middleName: "",
  lastName: "",
  dob: "",
  phone: "",
  email: "",
  addressLine1: "",
  addressLine2: "",
};

interface EnrichName {
  firstName: string;
  middleName: string;
  lastName: string;
}

interface EnrichAddress {
  firstReportedDate: string;
  lastReportedDate: string;
  street: string;
  unit: string;
  city: string;
  state: string;
  zip: string;
  propertyInfos?: Array<{
    poseidonId: number;
    latitude: string;
    longitude: string;
    isOwnerOccupied: boolean;
  }>;
}

interface EnrichPhone {
  number: string;
  type: string;
  isConnected: boolean;
  firstReportedDate: string;
  lastReportedDate: string;
}

interface EnrichEmail {
  email: string;
  isValidated: boolean;
  isBusiness: boolean;
}

interface EnrichRelative {
  estateId: string;
  name: EnrichName;
  age: string;
}

interface ConsumerProfileSummary {
  estimatedIncome: string;
  numberOfChildren: string;
  maritalStatus: string;
  occupationGroup: string;
  occupation: string;
  education: string;
  businessOwner: string;
  gender: string;
  netWorth: string;
  estimatedCurrentHomeValue: string;
  creditRating: string;
  ethnicity: string;
  ethnicGroup: string;
  language: string;
}

interface EnrichPlusPerson {
  estateId: string;
  name: EnrichName;
  age: string;
  partialDob: string;
  addresses: EnrichAddress[];
  phones: EnrichPhone[];
  emails: EnrichEmail[];
  relatives: EnrichRelative[];
  consumerProfileSummary: ConsumerProfileSummary;
}

interface EnrichPlusResponse {
  person: EnrichPlusPerson;
  identityScore: number;
  isError: boolean;
  error?: {
    message?: string;
    inputErrors?: string[];
  };
}

const formatName = (name: EnrichName) =>
  [name.firstName, name.middleName, name.lastName].filter(Boolean).join(" ");

const formatAddress = (addr: EnrichAddress) =>
  [addr.street, addr.unit].filter(Boolean).join(" ") + `; ${addr.city}, ${addr.state} ${addr.zip}`;

interface RequestSession {
  session_id: number;
  access_token: string;
}

const IntegrationsPage: React.FC = () => {
  const { person: appPerson } = useAppContext();
  const estateId = appPerson?.estate_id;

  const [form, setForm] = React.useState<SearchForm>(INITIAL_FORM);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [response, setResponse] = React.useState<EnrichPlusResponse | null>(null);

  const [drMaxUploads, setDrMaxUploads] = useState("10");
  const [drDocumentType, setDrDocumentType] = useState("");
  const [drExpiresMinutes, setDrExpiresMinutes] = useState("1440");
  const [drRecipientContactId, setDrRecipientContactId] = useState("");
  const [drSession, setDrSession] = useState<RequestSession | null>(null);
  const [drLoading, setDrLoading] = useState(false);
  const [drError, setDrError] = useState("");

  const updateField = (field: keyof SearchForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSearch = async () => {
    if (!form.firstName || !form.lastName) {
      setError("First name and last name are required");
      return;
    }

    setLoading(true);
    setError("");
    setResponse(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke("onboarding/person-search", {
        body: {
          FirstName: form.firstName,
          MiddleName: form.middleName || undefined,
          LastName: stripNameSuffix(form.lastName),
          Dob: form.dob || undefined,
          Phone: form.phone || undefined,
          Email: form.email || undefined,
          Address: form.addressLine1
            ? { AddressLine1: form.addressLine1, AddressLine2: form.addressLine2 }
            : undefined,
        },
      });

      if (fnError) {
        setError(fnError.message);
        return;
      }

      const typed = data as EnrichPlusResponse;
      setResponse(typed);

      if (typed.isError && typed.error) {
        const messages = [typed.error.message, ...(typed.error.inputErrors ?? [])].filter(Boolean);
        setError(messages.join(" | "));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Request failed");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRequest = async () => {
    if (!estateId) {
      setDrError("No active estate");
      return;
    }

    setDrLoading(true);
    setDrError("");
    try {
      const { data, error: fnError } = await supabase.functions.invoke("documents/request", {
        body: {
          estate_id: estateId,
          max_uploads: drMaxUploads !== "" ? parseInt(drMaxUploads) : 10,
          expires_in_minutes: drExpiresMinutes !== "" ? parseInt(drExpiresMinutes) : 1440,
          requested_document_type: drDocumentType || undefined,
          recipient_contact_id: drRecipientContactId ? parseInt(drRecipientContactId) : undefined,
        },
      });

      if (fnError) {
        const body =
          "context" in fnError
            ? await (fnError.context as Response).json().catch(() => null)
            : null;
        setDrError(body?.error || fnError.message);
        return;
      }

      setDrSession(data as RequestSession);
    } catch (err) {
      setDrError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setDrLoading(false);
    }
  };

  const person = response?.person;

  return (
    <div className="w-full px-12 py-12">
      <header className="mb-12">
        <p className="mb-1 text-sm font-medium text-brand">Design System</p>
        <h1 className="mb-3 font-sans text-2xl font-bold tracking-tight text-stone-900">
          Integrations
        </h1>
        <p className="text-sm leading-relaxed text-stone-500">
          Test pages for third-party API integrations.
        </p>
      </header>

      <div className="space-y-16">
        <section id="contact-enrich" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Contact Enrich Plus</h2>
            <p className="mt-1 text-sm text-stone-500">
              Enrich a contact via Tracers / EnformionGO. Returns the top match with addresses,
              phones, emails, and relatives.
            </p>
          </div>

          {/* Search Form */}
          <div className="grid max-w-2xl gap-4 sm:grid-cols-3">
            <div className="grid gap-1.5">
              <Label htmlFor="ps-first">First Name *</Label>
              <Input
                id="ps-first"
                value={form.firstName}
                onChange={(e) => updateField("firstName", e.target.value)}
                placeholder="First name"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="ps-middle">Middle Name</Label>
              <Input
                id="ps-middle"
                value={form.middleName}
                onChange={(e) => updateField("middleName", e.target.value)}
                placeholder="Middle name"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="ps-last">Last Name *</Label>
              <Input
                id="ps-last"
                value={form.lastName}
                onChange={(e) => updateField("lastName", e.target.value)}
                placeholder="Last name"
              />
            </div>
          </div>

          <div className="grid max-w-2xl gap-4 sm:grid-cols-3">
            <div className="grid gap-1.5">
              <Label htmlFor="ps-dob">Date of Birth</Label>
              <Input
                id="ps-dob"
                value={form.dob}
                onChange={(e) => updateField("dob", e.target.value)}
                placeholder="MM/DD/YYYY"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="ps-phone">Phone</Label>
              <Input
                id="ps-phone"
                value={form.phone}
                onChange={(e) => updateField("phone", e.target.value)}
                placeholder="(555) 555-5555"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="ps-email">Email</Label>
              <Input
                id="ps-email"
                type="email"
                value={form.email}
                onChange={(e) => updateField("email", e.target.value)}
                placeholder="email@example.com"
              />
            </div>
          </div>

          <div className="grid max-w-2xl gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor="ps-addr1">Address Line 1</Label>
              <Input
                id="ps-addr1"
                value={form.addressLine1}
                onChange={(e) => updateField("addressLine1", e.target.value)}
                placeholder="Street address"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="ps-addr2">Address Line 2</Label>
              <Input
                id="ps-addr2"
                value={form.addressLine2}
                onChange={(e) => updateField("addressLine2", e.target.value)}
                placeholder="City, State ZIP"
              />
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleSearch} disabled={loading}>
              {loading ? "Enriching..." : "Enrich Contact"}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setForm(INITIAL_FORM);
                setResponse(null);
                setError("");
              }}
            >
              Clear
            </Button>
          </div>

          {/* Error */}
          {error && (
            <div className="max-w-2xl rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
              {error}
            </div>
          )}

          {/* Result */}
          {person && (
            <div className="space-y-4">
              {/* Matched Person */}
              <div className="rounded-lg border border-stone-200 p-5 space-y-4">
                <div>
                  <div className="flex items-baseline gap-3">
                    <h3 className="text-base font-semibold text-stone-900">
                      {formatName(person.name)}
                    </h3>
                    {person.age && (
                      <span className="text-sm text-stone-500">
                        {person.age === "Deceased" ? "" : `Age ${person.age}`}
                      </span>
                    )}
                    {response.identityScore != null && (
                      <span className="text-2xs text-stone-400">
                        Match: {response.identityScore}%
                      </span>
                    )}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-stone-500">
                    {person.partialDob && <span>DOB: {person.partialDob}</span>}
                  </div>
                </div>

                {/* Contact Info */}
                <div className="grid gap-4 sm:grid-cols-3">
                  {person.addresses?.length > 0 && (
                    <div>
                      <p className="mb-1.5 text-2xs font-semibold uppercase tracking-wide text-stone-400">
                        Addresses
                      </p>
                      <div className="space-y-2 text-sm">
                        {person.addresses.slice(0, 3).map((addr, i) => (
                          <div key={i}>
                            <div className="text-stone-700">{formatAddress(addr)}</div>
                            <div className="text-2xs text-stone-400">
                              Last: {addr.lastReportedDate}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {person.phones?.length > 0 && (
                    <div>
                      <p className="mb-1.5 text-2xs font-semibold uppercase tracking-wide text-stone-400">
                        Phones
                      </p>
                      <div className="space-y-1.5 text-sm">
                        {person.phones.map((ph, i) => (
                          <div key={i}>
                            <span className="text-stone-700">{ph.number}</span>
                            <span className="ml-1.5 text-2xs text-stone-400">
                              {ph.type} {ph.isConnected ? "" : "(disconnected)"}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {person.emails?.length > 0 && (
                    <div>
                      <p className="mb-1.5 text-2xs font-semibold uppercase tracking-wide text-stone-400">
                        Emails
                      </p>
                      <div className="space-y-1 text-sm">
                        {person.emails.map((em, i) => (
                          <div key={i} className="text-stone-700">
                            {em.email}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Relatives */}
              {person.relatives?.length > 0 && (
                <div className="rounded-lg border border-stone-200 p-5">
                  <p className="mb-3 text-2xs font-semibold uppercase tracking-wide text-stone-400">
                    Associates / Family ({person.relatives.length})
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-stone-200 text-left text-2xs text-stone-400">
                          <th className="pb-2 pr-4 font-medium">Name</th>
                          <th className="pb-2 pr-4 font-medium">Age</th>
                        </tr>
                      </thead>
                      <tbody>
                        {person.relatives.map((rel) => (
                          <tr key={rel.estateId} className="border-b border-stone-50">
                            <td className="py-2 pr-4 font-medium text-stone-700">
                              {formatName(rel.name)}
                            </td>
                            <td className="py-2 pr-4 text-stone-500">
                              {rel.age === "Deceased" ? (
                                <span className="text-red-500">Deceased</span>
                              ) : (
                                rel.age || "-"
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Raw JSON */}
              <details className="max-w-4xl">
                <summary className="cursor-pointer text-sm font-medium text-stone-500 hover:text-stone-700">
                  Raw JSON Response
                </summary>
                <pre className="mt-2 max-h-96 overflow-auto rounded-lg bg-stone-50 p-4 text-2xs text-stone-700">
                  {JSON.stringify(response, null, 2)}
                </pre>
              </details>
            </div>
          )}
        </section>

        <section id="document-request-test" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Document Request</h2>
            <p className="mt-1 text-sm text-stone-500">
              Create a document request link and open it to test the upload flow. Estate ID:{" "}
              {estateId ?? "none"}
            </p>
          </div>

          <div className="grid max-w-2xl gap-4 sm:grid-cols-3">
            <div className="grid gap-1.5">
              <Label htmlFor="dr-max">Max Uploads</Label>
              <Input
                id="dr-max"
                type="number"
                value={drMaxUploads}
                onChange={(e) => setDrMaxUploads(e.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="dr-expires">Expires (minutes)</Label>
              <Input
                id="dr-expires"
                type="number"
                value={drExpiresMinutes}
                onChange={(e) => setDrExpiresMinutes(e.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="dr-type">Document Type (optional)</Label>
              <select
                id="dr-type"
                className="flex h-10 w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm"
                value={drDocumentType}
                onChange={(e) => setDrDocumentType(e.target.value)}
              >
                <option value="">None</option>
                {Object.values(DocumentType).map((value) => (
                  <option key={value} value={value}>
                    {getDocumentTypeLabel(value)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid max-w-2xl gap-4 sm:grid-cols-3">
            <div className="grid gap-1.5">
              <Label htmlFor="dr-contact">Recipient Contact ID (optional)</Label>
              <Input
                id="dr-contact"
                type="number"
                value={drRecipientContactId}
                onChange={(e) => setDrRecipientContactId(e.target.value)}
                placeholder="estate_contacts.id"
              />
            </div>
          </div>

          <Button onClick={handleCreateRequest} disabled={drLoading || !estateId}>
            {drLoading ? "Creating..." : "Create Request"}
          </Button>

          {drError && (
            <div className="max-w-2xl rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
              {drError}
            </div>
          )}

          {drSession && (
            <div className="max-w-2xl rounded-lg border border-green-200 bg-green-50 p-3 text-sm">
              <p className="font-medium text-green-800">Request created</p>
              <p className="mt-2 text-green-700">
                <a
                  href={`${window.location.origin}/document-request?token=${drSession.access_token}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  {window.location.origin}/document-request?token={drSession.access_token}
                </a>
              </p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default IntegrationsPage;
